import classNames from 'classnames'
import * as React from 'react'

import styles from '../../../../common/Sidebar/SidebarItem/SidebarItem.css'
import SidebarLink from '../../../../common/Sidebar/SidebarLink/SidebarLink'
import SvgIcon from '../../../../common/SvgIcon/SvgIcon'
import type {VirtualizedTreeNodeProps} from '../../../../common/VirtualizedTree/VirtualizedTree'
import VirtualizedTreeItem from '../../../../common/VirtualizedTree/VirtualizedTreeItem/VirtualizedTreeItem'
import VirtualizedTreeOverlayItem from '../../../../common/VirtualizedTree/VirtualizedTreeOverlayItem/VirtualizedTreeOverlayItem'
import type {AgentsTreeItemType} from '../AgentsSidebar.types'
import {AgentTreeItemType} from '../AgentsSidebar.types'
import DisconnectedAgentsCounter from '../DisconnectedAgentsCounter/DisconnectedAgentsCounter'
import {AgentsSidebarEditFavorite} from '../EditFavorite/AgentsSidebarEditFavorite'
import Title from '../Title/Title'

import Agent from './Agent/Agent'
import AgentPool from './AgentPool/AgentPool'
import {AgentSelectionCounter} from './AgentSelectionCounter/AgentSelectionCounter'
import AgentType from './AgentType/AgentType'

import itemStyles from './AgentsSidebarItem.css'

type Props = VirtualizedTreeNodeProps &
  AgentsTreeItemType & {
    itemKey: string
    className?: string | null | undefined
    setExpandState: (item: AgentsTreeItemType, expanded: boolean) => void
    onBlur?: (arg0: any) => void
  }
export const INDEX_ATTRIBUTE = 'data-index'
const popupHtmlProps = {
  trapFocus: false,
}
const AgentsSidebarItem: React.ComponentType<Props> = React.memo(function AgentsSidebarItem({
  className,
  onBlur,
  setExpandState,
  index,
  style,
  height,
  width,
  indent,
  itemKey,
  parent,
  cache,
  ...rest
}) {
  const item: AgentsTreeItemType = rest
  const expandable = 'expandable' in item && item.expandable
  const containsActive = 'containsActive' in item && item.containsActive

  const expanded = expandable && item.expanded
  const active =
    (item.type !== AgentTreeItemType.TITLE && item.active) ||
    (expanded !== true && expandable && containsActive)

  const handleToggleExpandItem = React.useCallback(() => {
    setExpandState(item, !expanded)
  }, [item, expanded, setExpandState])
  const wrapperHtmlProps = React.useMemo(
    () => ({
      tabIndex: 0,
      [INDEX_ATTRIBUTE]: index,
      onBlur,
    }),
    [index, onBlur],
  )

  switch (item.type) {
    case AgentTreeItemType.TITLE:
      return (
        <VirtualizedTreeItem
          containerClassName={itemStyles.container}
          className={classNames(className, styles.item, itemStyles.title)}
          wrapperClassName={styles.itemWrapper}
          contentClassName={itemStyles.favorite}
          style={style}
          indent={indent}
        >
          <Title {...item} />
          {item.editable && <AgentsSidebarEditFavorite className={itemStyles.favoriteButton} />}
        </VirtualizedTreeItem>
      )

    case AgentTreeItemType.FAVORITE_LINK:
      const count = item.disconnectedCount ?? 0
      return (
        <VirtualizedTreeItem
          className={classNames(className, styles.item, styles.hoverableItem, {
            [styles.activeTitle]: active,
            [styles.activeItem]: active,
          })}
          wrapperClassName={styles.itemWrapper}
          style={style}
          indent={indent}
          expandable={expandable}
          wrapperHtmlProps={wrapperHtmlProps}
        >
          <SidebarLink
            to={item.to}
            className={styles.hoverableLink}
            tail={count > 0 && <DisconnectedAgentsCounter count={count} />}
            tabIndex={-1}
            aria-label={item.title}
          >
            <SvgIcon className={itemStyles.linkIcon} icon="star-filled" />
            {item.title}
          </SidebarLink>
        </VirtualizedTreeItem>
      )

    case AgentTreeItemType.SELECTION:
      const tail = <AgentSelectionCounter item={item} />

      return (
        <VirtualizedTreeItem
          className={classNames(className, styles.item, {
            [styles.hoverableItem]: item.href,
            [styles.activeItem]: active,
          })}
          containerClassName={itemStyles.container}
          wrapperClassName={styles.itemWrapper}
          onToggleExpand={handleToggleExpandItem}
          expanded={expanded}
          style={style}
          indent={indent}
          expandable={item.expandable}
          wrapperHtmlProps={wrapperHtmlProps}
        >
          {item.href ? (
            <SidebarLink to={item.href} className={styles.hoverableLink} tabIndex={-1} tail={tail}>
              <Title {...item} />
            </SidebarLink>
          ) : (
            <>
              <Title {...item} />
              {tail}
            </>
          )}
        </VirtualizedTreeItem>
      )

    default:
      return (
        <VirtualizedTreeOverlayItem
          className={classNames(className, styles.item, styles.hoverableItem)}
          activeClassName={classNames(styles.activeTitle, styles.activeItem)}
          hoveredClassName={styles.hoveredItem}
          focusedClassName={styles.focusedItem}
          wrapperClassName={styles.itemWrapper}
          active={active}
          expanded={expanded}
          onToggleExpand={handleToggleExpandItem}
          popupHtmlProps={popupHtmlProps}
          itemType={item.type}
          itemKey={itemKey}
          wrapperHtmlProps={wrapperHtmlProps}
          index={index}
          style={style}
          height={height}
          width={width}
          indent={indent}
          parent={parent}
          cache={cache}
          expandable={expandable}
        >
          {item.type === AgentTreeItemType.POOL ? (
            <AgentPool className={styles.hoverableLink} {...item} />
          ) : null}
          {item.type === AgentTreeItemType.AGENT_TYPE ? (
            <AgentType className={styles.hoverableLink} {...item} />
          ) : null}
          {item.type === AgentTreeItemType.AGENT ? (
            <Agent className={styles.hoverableLink} {...item} />
          ) : null}
        </VirtualizedTreeOverlayItem>
      )
  }
})
export default AgentsSidebarItem
